import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Wallet, TrendingUp, Hourglass, Award, Sparkles, CheckCircle2, Users, Timer } from "lucide-react";
import ReferralShareCard from "../../components/ReferralShareCard";

function StatCard({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function ReferralLinkCard({ data }) {
  const code = data.referral_code || data.distributor?.referral_code;
  const link = data.referral_link || data.distributor?.referral_link;
  if (!code || !link) return null;
  return (
    <ReferralShareCard
      code={code}
      link={link}
      commissionRate={data.commission_rate || 30}
      planPrice={data.plan_price || 1500}
      referralRewardFixed={data.referral_reward_fixed || 500}
    />
  );
}

export default function DistributorOverview() {
  const [data, setData] = useState(null);
  const [leader, setLeader] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [dash, lb] = await Promise.all([
          api.get("/referrals/dashboard"),
          api.get("/referrals/leaderboard").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setData(dash.data);
        if (lb.data) setLeader(lb.data);
      } catch (err) {
        if (!cancelled) console.warn("[dist-overview] load failed:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return <div className="text-slate-500" data-testid="dist-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="dist-overview">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Award className="w-7 h-7 text-emerald-600" /> Referral Partner Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Your referral activity at a glance — {data.commission_rate}% recurring commission on every paid subscription.
        </p>
      </div>

      <ReferralLinkCard data={data} />

      {/* Referral Statistics — per new spec (Feb 2026):
          Active Referrals · Trial Accounts · Paid Shops · Earnings · Pending Commission */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} label="Active Referrals" value={data.shops_active ?? 0} hint="Signed up via your link" tone="sky" testid="dist-card-active-referrals" />
        <StatCard icon={Timer} label="Trial Accounts" value={data.trial_accounts ?? 0} hint="In 30-day free trial" tone="amber" testid="dist-card-trial-accounts" />
        <StatCard icon={CheckCircle2} label="Paid Shops" value={data.paid_shops ?? 0} hint="Upgraded to a paid plan" tone="emerald" testid="dist-card-paid-shops" />
        <StatCard icon={TrendingUp} label="Earnings" value={fmtPKR(data.commission_total)} hint={`${data.commission_rate}% recurring`} tone="emerald" testid="dist-card-earnings" />
        <StatCard icon={Hourglass} label="Pending Commission" value={fmtPKR(data.commission_pending)} hint="Approval in progress" tone="amber" testid="dist-card-pending" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-lg">Wallet</div>
              <div className="text-xs text-slate-500">Withdraw your earned commissions (min PKR {(data.min_withdrawal || 1000).toLocaleString()}).</div>
            </div>
            <Link to="/distributor/wallet">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dist-withdraw-btn">
                <Wallet className="w-4 h-4 mr-1.5" /> Withdraw
              </Button>
            </Link>
          </div>
          <div className="mt-5 flex items-baseline gap-2">
            <span className="font-display text-4xl font-bold tabular text-emerald-700">{fmtPKR(data.wallet_balance)}</span>
            <span className="text-sm text-slate-500">balance</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <div className="font-display font-semibold text-lg">How it works</div>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>1. Share your referral <b>link</b> or <b>code</b> — WhatsApp, Facebook or QR.</li>
            <li>2. Customer signs up khud on website / Android app (referral code enter karein).</li>
            <li>3. Woh 30-day FREE trial start karte hain — koi upfront charge nahi.</li>
            <li>4. Trial ke baad jab woh <b>Quarterly / Annual</b> plan choose karte hain, aap ko har payment par <b className="text-emerald-700">{data.commission_rate}% recurring commission</b> milta hai.</li>
            <li>5. PKR {(data.min_withdrawal || 1000).toLocaleString()}+ balance hone par kabhi bhi Withdraw karein.</li>
          </ul>
        </div>
      </div>

      {leader && leader.top?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-lg">Leaderboard</div>
              <div className="text-xs text-slate-500">Top referral partners by revenue. Aap ka rank: <b>#{leader.my_rank || "—"}</b></div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {leader.top.slice(0, 10).map((r) => (
              <div key={r.rank} className={`flex items-center gap-4 p-4 ${r.is_me ? "bg-sky-50" : ""}`} data-testid={`dist-leader-${r.rank}`}>
                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-sm font-bold tabular">{r.rank}</div>
                <div className="flex-1">
                  <div className="font-semibold">{r.name}{r.is_me && <span className="ml-2 text-xs text-sky-700">(you)</span>}</div>
                  <div className="text-xs text-slate-500">{r.shops_count} shops</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold tabular">{fmtPKR(r.revenue)}</div>
                  <div className="text-xs text-slate-500">revenue</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
